import React from 'react';
import GlobalTabs from '../GlobalTabs';
import SmallServiceCard from '../SmallServiceCard';
import styles from './OverviewTab.module.css';
import { useLocale } from '../../context/DynamicLocaleProvider';
import { useTheme } from '../../context/ThemeProvider';
import { Favicon } from '../../utils/favicon';

interface DocumentStatus {
  type: 'urs' | 'fs' | 'sops' | 'tests' | 'ra';
  status: 'complete' | 'in_review' | 'draft' | 'pending';
  completion: number;
  lastUpdated?: Date;
}

interface OverviewTabProps {
  documentStatuses: DocumentStatus[];
  complianceScore?: number;
  onExport?: () => void;
  onAiReview?: () => void;
  onGenerateReport?: () => void;
  projectName?: string;
  projectDescription?: string;
  companyWebsite?: string;
  companyName?: string;
  onDeleteProject?: () => void;
  onAskProjectAi?: () => Promise<void> | void;
}

export const OverviewTab: React.FC<OverviewTabProps> = ({
  documentStatuses,
  complianceScore,
  onExport,
  onAiReview,
  onGenerateReport,
  projectName,
  projectDescription,
  companyWebsite,
  companyName,
  onDeleteProject,
  onAskProjectAi
}) => {
  const { t } = useLocale();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';

  const getDocumentLabel = (type: string) => {
    const labels: Record<string, string> = {
      urs: t('project_page_tab_urs') || 'URS',
      fs: t('project_page_tab_fs') || 'FS',
      sops: t('project_page_tab_sops') || 'SOPs',
      tests: t('project_page_tab_tests') || 'Tests',
      ra: t('project_page_tab_ra') || 'RA'
    };
    return labels[type] || type.toUpperCase();
  };

  const tabs = [
    {
      label: t('project_tab_overview') || 'Overview',
      queryValue: 'overview',
      content: (
        <>
          {/* Status Cards */}
          <div className={styles.statusCards}>
            <div className={styles.statusCard}>
              <h3 className={styles.statusCardTitle}>
                {t('project_tab_overview_status') || 'Project Status'}
              </h3>
              <div className={styles.statusCardContent}>
                {complianceScore !== undefined && (
                  <div className={styles.complianceScore}>
                    <span className={styles.scoreLabel}>
                      {t('project_tab_compliance_score') || 'Compliance Score'}
                    </span>
                    <span className={styles.scoreValue}>{complianceScore}%</span>
                  </div>
                )}
              </div>
            </div>

            <div className={styles.statusCard}>
              <h3 className={styles.statusCardTitle}>
                {t('project_tab_overview_progress') || 'Progress'}
              </h3>
              <div className={styles.statusCardContent}>
                {documentStatuses.map((doc) => (
                  <div key={doc.type} className={styles.progressItem}>
                    <span className={styles.progressLabel}>{getDocumentLabel(doc.type)}</span>
                    <div className={styles.progressBar}>
                      <div
                        className={styles.progressFill}
                        style={{ width: `${doc.completion}%` }}
                      />
                    </div>
                    <span className={styles.progressPercent}>{doc.completion}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )
    },
    {
      label: t('project_tab_options') || 'Options',
      queryValue: 'options',
      content: (
        <div className={styles.optionsGrid}>

          {/* Export all (PDF) */}
          {onExport && (
            <SmallServiceCard
              id="export-all"
              title={t('project_tab_export_all') || 'Export All'}
              description={t('project_tab_export_all_desc') || 'Export the entire project with all data points as a PDF file.'}
              fileTypeIcon="/assets/graphics/pdf-icon.svg"
              serviceIcon=""
              buttonIcon={isDarkMode ? "/assets/icons/arrow.down.circle.white.svg" : "/assets/icons/arrow.down.circle.black.svg"}
              buttonText={t('project_tab_export') || 'Export'}
              buttonOnClick={onExport}
              disabled
            />
          )}

          {/* Export all (CSV) */}
          {onExport && (
            <SmallServiceCard
              id="export-all-csv"
              title={t('project_tab_export_all_csv') || 'Export All as CSV'}
              description={t('project_tab_export_all_csv_desc') || 'Export the entire project with all data points as a CSV file.'}
              fileTypeIcon={"/assets/graphics/csv_icon.svg"}
              serviceIcon=""
              buttonIcon={isDarkMode ? "/assets/icons/arrow.down.circle.white.svg" : "/assets/icons/arrow.down.circle.black.svg"}
              buttonText={t('project_tab_export') || 'Export'}
              buttonOnClick={onExport}
              disabled
            />
          )}

          {/* Ask Project AI */}
          {onAskProjectAi && (
            <SmallServiceCard
              id="ask-project-ai"
              title={t('project_tab_ask_project_ai') || 'Ask Project AI'}
              description={t('project_tab_ask_project_ai_desc') || 'Ask questions about this project. The AI will search through all project documents, URS, FS, RISKs, Tests, and SOPs to provide comprehensive answers.'}
              fileTypeIcon={isDarkMode ? "/assets/icons/apple.intelligence.svg" : "/assets/icons/apple.intelligence.black.svg"}
              serviceIcon=""
              buttonIcon={isDarkMode ? "/assets/icons/arrow.right.circle.svg" : "/assets/icons/arrow.right.circle.black.svg"}
              buttonText={t('project_tab_ask') || 'Ask'}
              buttonOnClick={onAskProjectAi}
            />
          )}

          {/* Delete project */}
          {onDeleteProject && (
            <SmallServiceCard
              id="delete-project"
              title={t('project_page_delete_project') || 'Delete Project'}
              description={t('project_page_delete_project_desc') || 'All data associated with this project will be permanently and irreversibly deleted.'}
              fileTypeIcon="/assets/icons/trash.red.svg"
              serviceIcon=""
              buttonIcon={isDarkMode ? "/assets/icons/trash.white.svg" : "/assets/icons/trash.black.svg"}
              buttonText={t('project_tab_delete') || 'Delete'}
              buttonOnClick={onDeleteProject}
              brandColor="var(--wild-watermelon)"
            />
          )}
        </div>
      )
    }
  ];

  return (
    <div className={styles.overviewTab}>

      {/* Project Header */}
      <div className={styles.projectHeader}>
        <div className={styles.projectHeaderTitle}>

          {/* Company icon */}
          {companyWebsite && (
            <div className={styles.projectHeaderFavicon}>
              <Favicon
                websiteUrl={companyWebsite}
                size={48}
                retinaMultiplier={4}
              />
            </div>
          )}

          {/* Company icon */}
          <h2 className={styles.projectCompanyName}>
            {companyName || t('project_page_unknown_name') || 'Unknown company'}
          </h2>
        </div>

        {/* Project name */}
        <h1 className={styles.projectTitle}>
          {projectName || t('project_page_unknown_name') || 'Unknown project'}
        </h1>

        {/* Project description */}
        {projectDescription && (
          <p className={styles.projectDescription}>{projectDescription}</p>
        )}
      </div>

      {/* Tabs */}
      <GlobalTabs tabs={tabs} queryParam="tab" defaultTabValue="overview" />
    </div>
  );
};

